/**
 * woDeleteNotes — pure text builders for the work-order delete confirmation
 * (WoDeleteDialog), fed by GET /workorders/:id/delete-preview. Shared by the
 * WO header delete and the /workorders row delete so both spell out the same
 * consequences: schedule released, quote link cleared, results affected.
 */
import type { WorkOrderDeletePreview } from '../../types/workOrder';

const plural = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "Schedule attached: 1 plan + 4 COMMITTED bookings + 2 tentative bookings, 3 staff assignments — …" */
export function buildScheduleNote(p: WorkOrderDeletePreview): string | null {
  const parts: string[] = [];
  if (p.plans.length > 0) parts.push(plural(p.plans.length, 'schedule plan'));
  if (p.committedBookings > 0) parts.push(`${p.committedBookings} COMMITTED booking${p.committedBookings === 1 ? '' : 's'}`);
  if (p.tentativeBookings > 0) parts.push(plural(p.tentativeBookings, 'tentative booking'));
  if (p.indexAllocations > 0) parts.push(plural(p.indexAllocations, 'index allocation'));
  if (parts.length === 0 && p.assignments === 0 && p.sourceAllocations === 0) return null;
  const staff = p.assignments + p.sourceAllocations;
  const staffNote = staff > 0 ? `, ${plural(staff, 'staff assignment')}` : '';
  return `Schedule attached: ${parts.join(' + ') || 'staffing only'}${staffNote} — deleting releases the machine/people time on every plan and the live board.`;
}

/** "Linked to quote Q-2026-0042 — the link will be cleared; the quote returns to Accepted …" */
export function buildQuoteNote(p: WorkOrderDeletePreview): string | null {
  if (p.linkedQuotes.length === 0) return null;
  const numbers = p.linkedQuotes.map((q) => q.quoteNumber).join(', ');
  return `Converted from quote ${numbers} — the conversion link will be cleared; the quote returns to Accepted and can be converted again.`;
}

/** "3 tests have entered results — the rows are soft-deleted (an admin can restore them)." */
export function buildResultsNote(p: WorkOrderDeletePreview): string | null {
  if (p.testsWithData === 0) return null;
  return `${plural(p.testsWithData, 'test')} ${p.testsWithData === 1 ? 'has' : 'have'} entered results — the rows are soft-deleted, not destroyed (an admin can restore them).`;
}

/**
 * All consequence lines for the dialog. `includeSchedule=false` lets the WO
 * header (which already prints its own schedule sentence from the live impact
 * probe) add only the quote + results lines.
 */
export function buildWoDeleteNotes(
  p: WorkOrderDeletePreview | null,
  opts: { includeSchedule?: boolean } = {},
): string[] {
  if (!p) return [];
  const notes = [
    opts.includeSchedule === false ? null : buildScheduleNote(p),
    buildQuoteNote(p),
    buildResultsNote(p),
  ];
  return notes.filter((n): n is string => Boolean(n));
}

/** Confirming must also release schedule when committed work or a locked plan is attached. */
export function requiresReleaseFromPreview(p: WorkOrderDeletePreview | null): boolean {
  if (!p) return false;
  return p.committedBookings > 0 || p.plans.some((plan) => plan.status === 'Submitted');
}
